import { useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import type { DetailedNutrition } from "@/mocks/products";

interface NutritionModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DetailedNutrition;
  productName: string;
}

const footnote = "% Valores Diários de referência com base em uma dieta de 2.000 kcal. Seus valores diários podem ser maiores ou menores dependendo de suas necessidades energéticas.";

export default function NutritionModal({ isOpen, onClose, data, productName }: NutritionModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab" && contentRef.current) {
        const focusable = contentRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      if (contentRef.current) {
        const firstBtn = contentRef.current.querySelector("button");
        if (firstBtn) (firstBtn as HTMLElement).focus();
      }
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen, handleKeyDown]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={overlayRef}
          onClick={handleOverlayClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="fixed inset-0 z-[9998] flex items-end lg:items-center justify-center bg-foreground-950/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="nutrition-modal-title"
        >
          {/* Desktop: centered modal | Mobile: bottom sheet */}
          <motion.div
            ref={contentRef}
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full lg:max-w-xl lg:rounded-2xl rounded-t-2xl lg:rounded-b-2xl bg-background-50 max-h-[88vh] lg:max-h-[85vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="shrink-0 flex items-center justify-between px-5 lg:px-6 pt-5 lg:pt-6 pb-4 border-b border-background-200/50">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-file-list-3-line text-lg"></i>
                </span>
                <div>
                  <h2
                    id="nutrition-modal-title"
                    className="text-lg font-heading font-semibold text-foreground-950 leading-tight"
                  >
                    Informações Nutricionais
                  </h2>
                  <p className="text-sm text-foreground-500">{productName}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                aria-label="Fechar informações nutricionais"
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 hover:bg-background-100 transition-colors cursor-pointer text-foreground-500 hover:text-foreground-800 shrink-0"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-5 lg:px-6 py-5 scroll-clean">
              {/* General Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <div className="p-3 rounded-xl bg-background-100 border border-background-200/50 text-center">
                  <span className="block text-[10px] text-foreground-400 uppercase tracking-wider mb-1">
                    Porção por embalagem
                  </span>
                  <span className="text-sm font-semibold text-foreground-900">
                    Cerca de 3
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-background-100 border border-background-200/50 text-center">
                  <span className="block text-[10px] text-foreground-400 uppercase tracking-wider mb-1">
                    Porção
                  </span>
                  <span className="text-sm font-semibold text-foreground-900">
                    40 g (1/3 da unidade)
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-background-100 border border-background-200/50 text-center">
                  <span className="block text-[10px] text-foreground-400 uppercase tracking-wider mb-1">
                    Validade
                  </span>
                  <span className="text-sm font-semibold text-foreground-900">
                    {data.shelfLife}
                  </span>
                </div>
              </div>

              {/* Conservation & Allergens */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                <div className="p-4 rounded-xl bg-accent-50 border border-accent-200/40 flex items-start gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 shrink-0">
                    <i className="ri-fridge-line"></i>
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-foreground-800 mb-0.5">
                      Conservação
                    </p>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {data.conservation}
                    </p>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-secondary-50 border border-secondary-200/40 flex items-start gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 shrink-0">
                    <i className="ri-alert-line"></i>
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-foreground-800 mb-0.5">
                      Alergênicos
                    </p>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {data.allergens}
                    </p>
                  </div>
                </div>
              </div>

              {/* Nutrition Table */}
              <div className="rounded-xl border border-background-200/50 overflow-hidden mb-4">
                {/* Table Header */}
                <div className="grid grid-cols-4 bg-primary-500">
                  <div className="px-3 py-2.5">
                    <span className="text-[11px] font-semibold text-background-50">
                      Nutriente
                    </span>
                  </div>
                  <div className="px-3 py-2.5 text-center">
                    <span className="text-[11px] font-semibold text-background-50">
                      100 g
                    </span>
                  </div>
                  <div className="px-3 py-2.5 text-center">
                    <span className="text-[11px] font-semibold text-background-50">
                      40 g
                    </span>
                  </div>
                  <div className="px-3 py-2.5 text-center">
                    <span className="text-[11px] font-semibold text-background-50">
                      %VD
                    </span>
                  </div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-background-200/30">
                  {data.rows.map((row, idx) => (
                    <div
                      key={idx}
                      className={`grid grid-cols-4 ${
                        idx % 2 === 0 ? "bg-background-50" : "bg-background-100"
                      }`}
                    >
                      <div className="px-3 py-2.5">
                        <span className="text-xs text-foreground-800 font-medium">
                          {row.nutrient}
                        </span>
                      </div>
                      <div className="px-3 py-2.5 text-center">
                        <span className="text-xs text-foreground-700">
                          {row.per100g}
                        </span>
                      </div>
                      <div className="px-3 py-2.5 text-center">
                        <span className="text-xs text-foreground-700 font-medium">
                          {row.per40g}
                        </span>
                      </div>
                      <div className="px-3 py-2.5 text-center">
                        <span className="text-xs text-foreground-700">
                          {row.vd}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footnote */}
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/30">
                <p className="text-[11px] text-foreground-500 leading-relaxed">
                  {footnote}
                </p>
              </div>
            </div>

            {/* Drag handle for mobile */}
            <div className="lg:hidden shrink-0 flex justify-center py-3 border-t border-background-200/30">
              <div className="w-10 h-1 rounded-full bg-background-300" />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}